import type { MetricColumn } from '@grovus/design-system'
import type { Dict } from '../i18n'
import { materialityBand } from '../lib/delta'
import { DEFAULT_CONFIDENCE_THRESHOLDS, type ConfidenceThresholds } from './confidence'
import { fill } from './format'
import type { SortKey } from './partition'

/** Every column the creatives table can show, in display order. */
export const COLUMN_KEYS = [
  'name', 'accounts', 'format', 'hypothesis', 'audience', 'spend', 'share', 'hook', 'hold', 'ctr',
  'purch', 'roas', 'adj', 'cpa', 'aov', 'cpc', 'cpm', 'activeDays', 'ads', 'verdict',
] as const satisfies readonly SortKey[]

export type ColumnKey = (typeof COLUMN_KEYS)[number]

/**
 * The two columns no reader can hide: the one that says which creative a row is and the
 * one that says what to do about it. They are also the first and the last column, which
 * is what lets `MetricTable` resolve the pins by position for every hidden set.
 */
export const PINNED_KEYS: readonly ColumnKey[] = ['name', 'verdict']

export function isColumnKey(value: unknown): value is ColumnKey {
  return typeof value === 'string' && (COLUMN_KEYS as readonly string[]).includes(value)
}

/**
 * The columns to show, in display order.
 *
 * The reader's choice is stored as what is hidden rather than what is shown, so a column
 * added in a later build appears instead of being silently left out of an old list. A
 * pinned key in the hidden set is ignored.
 */
export function visibleColumns(hidden: ReadonlySet<ColumnKey>): ColumnKey[] {
  return COLUMN_KEYS.filter((key) => PINNED_KEYS.includes(key) || !hidden.has(key))
}

/**
 * The column definitions in the active language, one list for the table and the chooser,
 * so a column cannot carry one label in its header and another in the list that hides it.
 *
 * Typed by `ColumnKey`, not string: the first column was keyed 'creative', which sorts
 * nothing, and only a cast on onSort let it compile. Now a key that is not a sort the
 * screen understands fails the type check.
 */
export function columnDefs(
  t: Dict,
  thresholds: ConfidenceThresholds = DEFAULT_CONFIDENCE_THRESHOLDS,
): Record<ColumnKey, MetricColumn & { key: ColumnKey }> {
  const defs: Array<MetricColumn & { key: ColumnKey }> = [
    // Pinned: at 1440 the eighteen columns do not fit, and the two that have to stay in
    // view are the one that says which creative a row is and the one that says what to do
    // about it. Everything between them scrolls (GRO-32).
    { key: 'name', header: t.creative, sortable: true, pinned: 'start', hint: { text: t.hintShape, name: t.hintNameShape } },
    { key: 'accounts', header: t.accounts, sortable: true },
    // The Format axis (GRO-174): image, video or carousel, one of the five fixed axes and the
    // same three values the Format filter beside the table names.
    { key: 'format', header: t.format, sortable: true },
    // The «Идея и теги» chips (GRO-170): the idea and the visual in one cell, sorted by idea.
    { key: 'hypothesis', header: t.ideaTagsCol, sortable: true },
    // The Audience axis (GRO-175): the audience of the creative's ads, from their ad sets.
    { key: 'audience', header: t.audience, sortable: true },
    { key: 'spend', header: t.spend, numeric: true, sortable: true },
    { key: 'share', header: t.shareCol, numeric: true, sortable: true, hint: { text: t.hintShare, name: t.hintNameShare } },
    { key: 'hook', header: t.hookCol, numeric: true, sortable: true, hint: { text: t.hintFun, name: t.hintNameFun } },
    { key: 'hold', header: t.holdCol, numeric: true, sortable: true, hint: { text: t.hintFun, name: t.hintNameFun } },
    { key: 'ctr', header: t.sortCtr, numeric: true, sortable: true },
    { key: 'purch', header: t.convCol, numeric: true, sortable: true, hint: { text: t.hintPurch, name: t.hintNamePurch } },
    { key: 'roas', header: t.roasCol, numeric: true, sortable: true, hint: { text: t.hintRoas, name: t.hintNameRoas } },
    { key: 'adj', header: t.adj, numeric: true, sortable: true, hint: { text: t.hintAdj, name: t.hintNameAdj } },
    { key: 'cpa', header: t.cpaCol, numeric: true, sortable: true },
    { key: 'aov', header: t.aovCol, numeric: true, sortable: true, hint: { text: t.hintAov, name: t.hintNameAov } },
    { key: 'cpc', header: t.cpcCol, numeric: true, sortable: true },
    { key: 'cpm', header: t.cpmCol, numeric: true, sortable: true },
    { key: 'activeDays', header: t.activeDaysCol, numeric: true, sortable: true },
    { key: 'ads', header: t.adsCol, numeric: true, sortable: true },
    { key: 'verdict', header: t.verdictCol, pinned: 'end', hint: { text: verdictLegend(t, thresholds), name: t.hintNameVerdict } },
  ]
  return Object.fromEntries(defs.map((def) => [def.key, def])) as Record<ColumnKey, MetricColumn & { key: ColumnKey }>
}

/** The example sample size of the gap line: about the conversions of a creative that has run a while. */
const GAP_EXAMPLE_N = 140

/** The «Вывод» header legend: the sidebar's reliability rows, then the gap one example n needs. */
export function verdictLegend(t: Dict, thresholds: ConfidenceThresholds): string {
  return [
    `■■■ ${t.relReliable} - n ≥ ${thresholds.reliableAt}`,
    `■■□ ${t.relProvisional} - n ${thresholds.provisionalAt}-${thresholds.reliableAt - 1}`,
    `■□□ ${t.relInsufficient} - n < ${thresholds.provisionalAt}`,
    fill(t.verdictGapLine, { n: GAP_EXAMPLE_N, b: Math.round(materialityBand(GAP_EXAMPLE_N)) }),
  ].join('\n')
}
